/**
 * Encodes an array of booleans into a Run-Length Encoded (RLE) array of numbers.
 * The first number is always the count of initial `false` values.
 *
 * @param actions The input array of booleans.
 * @returns An array of numbers representing the run lengths.
 */
export const encodeActionsToRLE = (actions: boolean[]): number[] => {
  if (!actions || actions.length === 0) {
    return [];
  }

  const rleCounts: number[] = [];
  let countingState = false; // Always start by counting 'false'
  let count = 0;

  for (const action of actions) {
    if (action === countingState) {
      count++;
    } else {
      rleCounts.push(count);
      countingState = !countingState;
      count = 1;
    }
  }
  // Push the final count
  rleCounts.push(count);

  return rleCounts;
};

/**
 * Decodes a Run-Length Encoded (RLE) array of numbers back into an array of booleans.
 * Assumes the first count is for `false` values.
 *
 * @param rleCounts The RLE array of numbers (can be a standard array or a TypedArray).
 * @returns The original array of booleans.
 */
export const decodeRLEToActions = (
  rleCounts: number[] | Uint16Array,
): boolean[] => {
  const actions: boolean[] = [];

  rleCounts.forEach((count, index) => {
    // The value is 'false' for even indices (0, 2, 4...) and 'true' for odd indices (1, 3, 5...)
    const value = index % 2 !== 0;
    for (let i = 0; i < count; i++) {
      actions.push(value);
    }
  });

  return actions;
};
